import { RefObject, useCallback, useEffect, useRef, useState } from "react";

import { usePrevious } from "common/hooks";

import { cycleIndex } from "./arrays";
import { Optional } from "./types";

const noop = () => null;

/**
 *
 * @param rects An array of rectangle objects with at least a width property set
 * @param overflowWidth The point at which a rect is considered overflown
 * @returns The inclusive index of the first rect to overflow the specified width.
 */
export const findOverflowRectIndex = (rects: Pick<DOMRect, "width">[], overflowWidth: number) => {
  let accumulatedWidth = 0;

  for (let i = 0; i < rects.length; i += 1) {
    const rect = rects[i];
    accumulatedWidth += rect.width;

    if (accumulatedWidth >= overflowWidth) {
      return i;
    }
  }

  return rects.length;
};

export type UseIntersectionObserverOpts = Partial<IntersectionObserverInit> & {
  fireOnce?: boolean;
};

const defaultOpts = {};

/**
 * A hook that handles creating an IntersectionObserver and calling a callback function
 * when a given element scrolls into view
 *
 * @param ref A ref for the element to observe for intersection
 * @param onIntersect Callback for when the element becomes visible
 * @param options.fireOnce Whether the callback should only be fired the first
 * time the element becomes visible (optional, defaults to true)
 */
export const useIntersectionObserver = (
  ref: RefObject<HTMLElement | null>,
  onIntersect: () => void,
  opts: UseIntersectionObserverOpts = defaultOpts,
) => {
  const { fireOnce = true, rootMargin, threshold, root } = opts;
  useEffect(() => {
    const currentRef = ref.current;

    if (!currentRef) {
      return () => undefined;
    }

    const onVisible: IntersectionObserverCallback = (entries, observer) => {
      if (entries.length && entries[0].isIntersecting) {
        onIntersect();
        if (currentRef && fireOnce) {
          observer.unobserve(currentRef);
        }
      }
    };

    // TODO Potential optimization - only use a single observer through React context/provider
    // if we ever see drops in performance
    const observer = new IntersectionObserver(onVisible, {
      rootMargin: rootMargin || "0px",
      root,
      threshold,
    });

    observer.observe(currentRef);

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, onIntersect, rootMargin, threshold, fireOnce]);
};

export type OptionKeyboardNavigationOpts = {
  /** The default and lowerbound index value */
  minimumIndex?: number;
  /** How many additional navigation options there are in the consumer */
  extraIndexes?: number;
  /** Whether 'enter' should trigger onSelect */
  isSelectable?: boolean;
  /** Additional action that should trigger when the first of the extra indices is entered */
  onEnterExtraIndexes?: (activeIndex: number) => void;
  /** Additional action that should trigger when the last of the extra indices is exited */
  onLeaveExtraIndexes?: (activeIndex: number) => void;
  /** Always called when enter is hit, even if there's no valid option */
  onEnter?: (index: number, event: React.KeyboardEvent<HTMLElement>) => void;
};

export type OptionKeyboardNavigationControl = {
  activeIndex: number;
  handleKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  resetIndex: () => void;
  setIndex: (index: number) => void;
  setItemRef: (index: number) => (el: HTMLButtonElement) => void;
};

/**
 * A hook that manages arrow/enter input when navigating a list of options
 */
export const useOptionKeyboardNavigation = <T>(
  options: T[],
  onSelect: (option: T, event: React.KeyboardEvent<HTMLElement>) => void,
  {
    minimumIndex = -1,
    extraIndexes = 0,
    isSelectable = true,
    onEnterExtraIndexes,
    onLeaveExtraIndexes,
    onEnter = noop,
  }: OptionKeyboardNavigationOpts = {},
): OptionKeyboardNavigationControl => {
  const [activeIndex, setActiveIndex] = useState(minimumIndex);
  const prevIndex = usePrevious(activeIndex);
  const itemRefs = useRef<HTMLButtonElement[]>([]);

  useEffect(() => {
    const activeButton = itemRefs.current[activeIndex];
    activeButton?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const setItemRef = useCallback(
    (index: number) => (el: HTMLButtonElement) => {
      itemRefs.current[index] = el;
    },
    [],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const cycleActiveIndex = (intendedIndex: number) => {
        setActiveIndex((i) =>
          cycleIndex(minimumIndex, options.length + extraIndexes, i + intendedIndex),
        );
      };
      const { key } = event;

      switch (key) {
        case "ArrowDown":
          event.preventDefault(); // Don't move the cursor to the end
          return cycleActiveIndex(+1);
        case "ArrowUp":
          event.preventDefault(); // Don't move the cursor to the beginning
          return cycleActiveIndex(-1);
        case "Enter":
          // Don't trigger any extra newlines if we move to an input that accepts them
          event.preventDefault();
          onEnter(activeIndex, event);
          if (isSelectable && activeIndex >= 0 && activeIndex < options.length) {
            onSelect(options[activeIndex], event);
          }
          return null;
        case "Shift":
          return null;
        default:
          return setActiveIndex(minimumIndex); // Deselect if the user is typing
      }
    },
    [minimumIndex, activeIndex, options, onSelect, onEnter, extraIndexes, isSelectable],
  );

  useEffect(() => {
    if (
      activeIndex >= options.length &&
      (prevIndex ?? minimumIndex) < options.length &&
      onEnterExtraIndexes
    ) {
      onEnterExtraIndexes(activeIndex);
    }

    if (
      activeIndex < options.length &&
      (prevIndex ?? minimumIndex) >= options.length &&
      onLeaveExtraIndexes
    ) {
      onLeaveExtraIndexes(activeIndex);
    }
  }, [
    minimumIndex,
    activeIndex,
    onEnterExtraIndexes,
    onLeaveExtraIndexes,
    options.length,
    prevIndex,
  ]);

  return {
    activeIndex,
    handleKeyDown,
    resetIndex: useCallback(() => setActiveIndex(minimumIndex), [minimumIndex]),
    setIndex: setActiveIndex,
    setItemRef,
  };
};

export const FOCUSABLE_SELECTOR = "a[href], button, input, textarea, select, details, [tabindex]";

/**
 * Returns the children of a given (ref) element that can be focused
 */
export const getFocusableChildren = (ref: RefObject<HTMLElement | null>): HTMLElement[] => {
  const focusable = ref.current?.querySelectorAll(FOCUSABLE_SELECTOR);
  const focusableElems = [...(focusable ?? [])].flatMap((elem) =>
    elem instanceof HTMLElement && elem.getAttribute("tabindex") !== "-1" ? [elem] : [],
  );

  return [...focusableElems];
};

/**
 * Returns the first child of a given (ref) element that can be focused
 */
export const getFirstFocusableChild = (
  ref: RefObject<HTMLElement | null>,
): HTMLElement | undefined => {
  return getFocusableChildren(ref)[0];
};

/**
 * Matches a the first element in a list which is to the right of the client's x position.
 * Returns the index of that element.
 * If the x position is to the right of all elements, -1 is returned.
 *
 * e.g. if elements a, b, and c are displayed left to right, and the x position is between b and c,
 * b will be returned.
 */
export const getFirstElemIndexGreaterThanClientX = (
  elems: Pick<DOMRect, "right">[],
  clientX: number,
) => {
  return elems.reduce<number>((matching, current, i) => {
    if (matching !== -1) {
      return matching;
    }
    return current.right >= clientX ? i : matching;
  }, -1);
};

/**
 * Sets the document title to the provided string
 * Each page can only have this running once
 */
export const useDocumentTitle = (title: Optional<string>) => {
  useEffect(() => {
    if (title) {
      document.title = title;
    }
  }, [title]);
};

export const isInIframe = () => window.self !== window.top;

/**
 * Copies text to the clipboard, gracefully degrading when the async Clipboard
 * API is unavailable or rejects (e.g. the document isn't focused, a
 * permissions policy blocks it, or the browser doesn't support it). Falls back
 * to a hidden textarea + `document.execCommand("copy")`, which runs
 * synchronously inside the triggering user gesture and so succeeds in many of
 * the cases the async API refuses.
 *
 * Never throws — returns whether the copy succeeded so callers can surface a
 * "copy manually" message instead of leaking an unhandled `NotAllowedError`.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the legacy path below.
    }
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);

    textarea.select();
    textarea.setSelectionRange(0, text.length);

    const succeeded = document.execCommand("copy");
    document.body.removeChild(textarea);
    return succeeded;
  } catch {
    return false;
  }
};

/**
 * Returns all focusable elements in the document
 */
export const getAllFocusableElements = (): HTMLElement[] => {
  const focusable = document.querySelectorAll(FOCUSABLE_SELECTOR);
  const focusableElems = [...focusable].flatMap((elem) =>
    elem instanceof HTMLElement && elem.getAttribute("tabindex") !== "-1" ? [elem] : [],
  );

  return [...focusableElems];
};

/**
 * Returns the previous focusable element relative to the current element
 * @param currentElement The current element to find the previous focusable element from
 * @returns The previous focusable element, or null if none found
 */
export const getPreviousFocusableElement = (
  currentElement: Optional<HTMLElement>,
): HTMLElement | null => {
  if (!currentElement) {
    return null;
  }

  const allFocusable = getAllFocusableElements();
  const currentIndex = allFocusable.indexOf(currentElement);

  if (currentIndex <= 0) {
    return null;
  }

  return allFocusable[currentIndex - 1];
};

/**
 * Returns the next focusable element relative to the current element
 * @param currentElement The current element to find the next focusable element from
 * @returns The next focusable element, or null if none found
 */
export const getNextFocusableElement = (
  currentElement: Optional<HTMLElement>,
): HTMLElement | null => {
  if (!currentElement) {
    return null;
  }

  const allFocusable = getAllFocusableElements();
  const currentIndex = allFocusable.indexOf(currentElement);

  if (currentIndex === -1 || currentIndex >= allFocusable.length - 1) {
    return null;
  }

  return allFocusable[currentIndex + 1];
};
